const ADMISSION_STATUS_ORDER = [
  "DRAFT",
  "CONFIRMED",
  "DOCUMENTS_PENDING",
  "DOCUMENTS_UNDER_REVIEW",
  "DOCUMENTS_REJECTED",
  "DOCUMENTS_APPROVED",
  "PAYMENT_PENDING",
  "PAYMENT_INITIATED",
  "PAYMENT_SUCCESS",
  "PAYMENT_FAILED",
  "RECEIPT_GENERATED",
  "COMPLETED",
];

export const DOCUMENT_RULES = {
  studentPhoto: { label: "Student Photo", maxBytes: 2 * 1024 * 1024, required: true },
  aadharCard: { label: "Aadhaar Card Copy", maxBytes: 5 * 1024 * 1024, required: true },
  birthCertificate: { label: "Birth Certificate", maxBytes: 10 * 1024 * 1024, required: false },
  transferCertificate: { label: "Transfer Certificate", maxBytes: 10 * 1024 * 1024, required: false },
  previousMarksheet: { label: "Previous Marksheet", maxBytes: 10 * 1024 * 1024, required: false },
  addressProof: { label: "Address Proof", maxBytes: 10 * 1024 * 1024, required: false },
};

export const TOTAL_UPLOAD_QUOTA_BYTES = 100 * 1024 * 1024;

export const formatBytes = (value = 0) => {
  if (!value) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let size = Number(value);
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }

  return `${size >= 10 || unitIndex === 0 ? size.toFixed(0) : size.toFixed(1)} ${units[unitIndex]}`;
};

export const formatCurrency = (value, currency = "INR") => {
  const amount = Number(value || 0);

  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `Rs ${amount}`;
  }
};

export const normalizeAdmissionStatus = (record = {}) => {
  const raw = String(
    record.status
      || record.applicationStatus
      || record.admissionStatus
      || (record.paymentId ? "PAYMENT_SUCCESS" : "DRAFT")
  )
    .trim()
    .toUpperCase();

  return ADMISSION_STATUS_ORDER.includes(raw) ? raw : "DRAFT";
};

export const getStatusTone = (status) => {
  switch (status) {
    case "COMPLETED":
    case "RECEIPT_GENERATED":
    case "PAYMENT_SUCCESS":
    case "DOCUMENTS_APPROVED":
      return "border-emerald-200 bg-emerald-50 text-emerald-700";
    case "PAYMENT_FAILED":
    case "DOCUMENTS_REJECTED":
      return "border-rose-200 bg-rose-50 text-rose-700";
    case "PAYMENT_INITIATED":
    case "DOCUMENTS_UNDER_REVIEW":
      return "border-amber-200 bg-amber-50 text-amber-700";
    case "CONFIRMED":
    case "DOCUMENTS_PENDING":
    case "PAYMENT_PENDING":
      return "border-blue-200 bg-blue-50 text-blue-700";
    default:
      return "border-slate-200 bg-slate-50 text-slate-700";
  }
};

export const getNextAction = (status) => {
  switch (status) {
    case "DRAFT":
      return "Complete all required fields and confirm the application.";
    case "CONFIRMED":
    case "DOCUMENTS_PENDING":
      return "Upload the required documents to move the application forward.";
    case "DOCUMENTS_UNDER_REVIEW":
      return "Wait for school review. Re-upload only if a document is rejected.";
    case "DOCUMENTS_REJECTED":
      return "Replace the rejected documents and resubmit them.";
    case "DOCUMENTS_APPROVED":
    case "PAYMENT_PENDING":
      return "Proceed to payment using the backend-calculated amount.";
    case "PAYMENT_INITIATED":
      return "Finish the payment flow and wait for confirmation.";
    case "PAYMENT_FAILED":
      return "Retry payment or contact support if the issue continues.";
    case "PAYMENT_SUCCESS":
      return "Receipt generation is in progress.";
    case "RECEIPT_GENERATED":
    case "COMPLETED":
      return "Download your receipt or review your completed application.";
    default:
      return "Review your application details.";
  }
};

export const getTimelineSteps = (status) => {
  const normalized = normalizeAdmissionStatus({ status });
  const currentIndex = ADMISSION_STATUS_ORDER.indexOf(normalized);

  return [
    { key: "DRAFT", label: "Draft saved" },
    { key: "CONFIRMED", label: "Application confirmed" },
    { key: "DOCUMENTS_APPROVED", label: "Documents approved" },
    { key: "PAYMENT_SUCCESS", label: "Payment successful" },
    { key: "RECEIPT_GENERATED", label: "Receipt ready" },
  ].map((step) => ({
    ...step,
    done: ADMISSION_STATUS_ORDER.indexOf(step.key) <= currentIndex,
    current: step.key === normalized,
  }));
};

export const normalizeAdmissionRecord = (record = {}) => {
  const status = normalizeAdmissionStatus(record);
  const baseAmount = Number(
    record.baseAmount
      || record.originalAmount
      || record.admissionAmount
      || record.amount
      || 0
  );
  const waiverAmount = Number(record.waiverAmount || record.discountAmount || 0);
  const finalAmount = Number(record.finalAmount || record.payableAmount || baseAmount - waiverAmount || 0);

  return {
    ...record,
    status,
    baseAmount,
    waiverAmount,
    finalAmount,
    currency: record.currency || "INR",
    paymentId: record.paymentId || record.providerPaymentId || "",
    receiptNumber: record.receiptNumber || record.receiptId || "",
    rejectionReason: record.rejectionReason || record.reviewReason || record.note || "",
  };
};